import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axiosInstance from '../../../api/axiosInstance';
import CustomerDashboard from '../../admin/all_customers/CustomerDashboard';
import { createPortal } from 'react-dom';
import ShareModal from '../../../modals/ShareModal';

// ── REDESIGNED DOWNLOAD TOAST ───────────────────────────────────────────────
const DownloadToast = ({ show, filename = "Export.csv", onClose }) => {
  useEffect(() => {
    if (show) { const t = setTimeout(onClose, 3500); return () => clearTimeout(t); }
  }, [show, onClose]);

  if (!show) return null;

  return createPortal(
    // Positioned at bottom-right with safe area spacing
    <div className="fixed bottom-4 right-4 sm:bottom-8 sm:right-6 z-[100000] animate-in slide-in-from-bottom-6 sm:slide-in-from-right-8 fade-in duration-300 flex justify-end">
      {/* Animation grows from 0% to 100% to move left-to-right */}
      <style>{`@keyframes fillRight{from{width:0%}to{width:100%}}.fill-right{animation:fillRight 3.5s linear forwards}`}</style>
      
      {/* Boxy Green Container - Shrunken for mobile */}
      <div className="flex items-center gap-3 sm:gap-4 p-3.5 sm:p-5 relative rounded-xl shadow-2xl bg-emerald-600 border border-emerald-500 w-[calc(100vw-2rem)] max-w-[320px] sm:max-w-[340px] sm:w-80 overflow-hidden">
        
        {/* White Progress Line anchored to the left */}
        <span className="absolute h-1.5 bottom-0 left-0 fill-right bg-white shadow-[0_0_8px_rgba(255,255,255,0.8)]" />
        
        {/* White Success Icon inside a translucent circle */}
        <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white/20 text-white border border-white/30 backdrop-blur-sm">
          <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"/></svg>
        </div>
        
        {/* Text Details */}
        <div className="flex-1 min-w-0">
          <p className="text-xs sm:text-sm font-extrabold text-white truncate tracking-wide">Export Complete</p>
          <p className="text-[10px] sm:text-xs text-emerald-100 font-medium truncate mt-0.5">{filename}</p>
        </div>
        
        {/* Close Button */}
        <button onClick={onClose} className="p-1.5 sm:p-2 text-white/70 hover:text-white hover:bg-white/20 transition-colors rounded-lg flex-shrink-0">
          <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
        </button>
      </div>
    </div>,
    document.body
  );
};

const MyCustomers = () => {
  // --- Core State ---
  const [allCustomers, setAllCustomers] = useState([]); 
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // --- Pagination ---
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const PAGE_SIZE = 20;

  // --- Filter & Search ---
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterAccount, setFilterAccount] = useState('');
  const [filterDue, setFilterDue] = useState('');
  const [filterChilds, setFilterChilds] = useState('');
  const [filterInstallment, setFilterInstallment] = useState(''); 

  // --- Modal States ---
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false); 
  const [copySuccess, setCopySuccess] = useState(false);
  const [downloadToast, setDownloadToast] = useState({ show: false, filename: '' });
  
 // Set a temporary fallback or loading text
 const [referralCode, setReferralCode] = useState('Loading...'); 
 const inviteLink = `https://rkvisionindia.com/register/${referralCode}`;

  const hasActiveFilters = search || filterAccount || filterDue || filterChilds || filterInstallment;

  // ── Debounce search 400ms ─────────────────────────────────────────────────
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  // ── Fetch ALL records from API ───────────────────────────────────────────
  const fetchCustomers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get('/child/all', {
        params: { page, limit: PAGE_SIZE },
      });
      const { data, metadata } = response.data;
      setAllCustomers(data || []);
      if (metadata) {
        setTotalRecords(metadata.total);
        setTotalPages(Math.ceil(metadata.total / metadata.limit));
      }
    } catch (err) {
      console.error('Failed to fetch customers:', err);
      setError('Failed to load customer data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

 // ── Fetch Referral Code ──────────────────────────────────────────────────
 useEffect(() => {
  const fetchReferralCode = async () => {
    try {
      const response = await axiosInstance.get('/user/refer');
      
      // Let's cast a wider net to find the code
      const code = response.data?.refer; 
                // In case the response is just { data: "CAP-123" }

      if (code && typeof code === 'string') {
        setReferralCode(code);
      } else {
        // If it still can't find it, it will change to this so we know it failed the check
        setReferralCode('CODE_NOT_FOUND'); 
      }
    } catch (err) {
      console.error('Failed to fetch referral code:', err);
      setReferralCode('API_ERROR'); 
    }
  };

  fetchReferralCode();
}, []);

  // ── Client-side filtering ────────────────────────────────────────────────
  const filteredCustomers = allCustomers.filter((user) => {
    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      const nameMatch  = user.name?.toLowerCase().includes(q);
      const emailMatch = user.email?.toLowerCase().includes(q);
      const phoneMatch = user.phone?.toString().includes(q);
      if (!nameMatch && !emailMatch && !phoneMatch) return false;
    }

    if (filterAccount) {
      if (user.account?.toLowerCase() !== filterAccount.toLowerCase()) return false;
    }

    if (filterDue) {
      const due = Number(user.due) || 0;
      if (filterDue === '>1'  && !(due >= 1  && due <= 4))  return false;
      if (filterDue === '>5'  && !(due >= 5  && due <= 9))  return false;
      if (filterDue === '>10' && !(due >= 10 && due <= 14)) return false;
      if (filterDue === '>15' && !(due >= 15 && due <= 19)) return false;
      if (filterDue === '>20' && !(due >= 20))              return false;
    }

    if (filterChilds) {
      const child = Number(user.child) || 0;
      if (filterChilds === '0' && child !== 0) return false;
      if (filterChilds === '1-50' && (child < 1 || child > 50)) return false;
      if (filterChilds === '51-100' && (child < 51 || child > 100)) return false;
      if (filterChilds === '101-200' && (child < 101 || child > 200)) return false;
      if (filterChilds === '201-500' && (child < 201 || child > 500)) return false;
      if (filterChilds === '500+' && child <= 500) return false;
    }

    if (filterInstallment) {
      const inst = Number(user.installments) || 0;
      if (filterInstallment === '0' && inst !== 0) return false;
      if (filterInstallment === '1-5' && (inst < 1 || inst > 5)) return false;
      if (filterInstallment === '6-10' && (inst < 6 || inst > 10)) return false;
      if (filterInstallment === '11-15' && (inst < 11 || inst > 15)) return false;
      if (filterInstallment === '16-19' && (inst < 16 || inst > 19)) return false;
      if (filterInstallment === '20-24' && (inst < 20 || inst > 24)) return false;
    }

    return true;
  });

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleFilterChange = (setter) => (e) => {
    setter(e.target.value);
    setPage(1);
  };

  const handleResetFilters = () => {
    setSearch('');
    setDebouncedSearch('');
    setFilterAccount('');
    setFilterDue('');
    setFilterChilds('');
    setFilterInstallment('');
    setPage(1);
    setIsMobileFilterOpen(false); 
  };

  const handleExportCSV = () => {
    if (!filteredCustomers || filteredCustomers.length === 0) return;
    const headers = ['Sr No', 'Name', 'Email', 'Phone', 'Address', 'City', 'State', 'Account Status', 'Joined Date', 'Installments Paid', 'Due Installments', 'Childs'];
    const csvRows = filteredCustomers.map((user, index) => [
      (page - 1) * PAGE_SIZE + index + 1, `"${user.name || ''}"`, `"${user.email || ''}"`, `"${user.phone || ''}"`,
      `"${user.address || ''}"`, `"${user.city || ''}"`, `"${user.state || ''}"`,
      `"${user.account || ''}"`, `"${new Date(user.created_at).toLocaleDateString()}"`,
      user.installments || 0, user.due || 0, user.child || 0,
    ].join(','));
    
    const csvContent = [headers.join(','), ...csvRows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.style.display = 'none'; 
    link.href = url;
    
    const safeFilename = `My_Network_Export_${new Date().toISOString().slice(0, 10)}.csv`;
    link.download = safeFilename;
    document.body.appendChild(link);
    link.click();
    
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setDownloadToast({ show: true, filename: safeFilename });
    }, 100); 
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(inviteLink);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(dateString));
  };

  const getStatusBadge = (status) => {
    const s = status?.toLowerCase() || '';
    if (s === 'active' || s === 'completed' || s === 'approved')
      return <span className="px-2.5 py-1 rounded text-[11px] font-bold bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 uppercase tracking-wider">{status}</span>;
    if (s === 'pending' || s === 'prospect')
      return <span className="px-2.5 py-1 rounded text-[11px] font-bold bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 uppercase tracking-wider">{status}</span>;
    if (s === 'none')
      return <span className="px-2.5 py-1 rounded text-[11px] font-bold bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300 uppercase tracking-wider">{status}</span>;
    return <span className="px-2.5 py-1 rounded text-[11px] font-bold bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 uppercase tracking-wider">{status}</span>;
  };

  // ── REUSABLE FILTER SELECTS ──
  const FilterSelects = ({ mobile = false }) => {
    const selectClass = mobile 
      ? "w-full py-2.5 px-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md text-sm outline-none focus:border-indigo-500 dark:text-white"
      : "py-2 px-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md text-sm outline-none focus:border-indigo-500 dark:text-white min-w-[120px]";

    return (
      <>
        {mobile && <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-2 block">Account Status</label>}
        <select value={filterAccount} onChange={handleFilterChange(setFilterAccount)} className={selectClass}>
          <option value="">All Accounts</option>
          <option value="active">Active</option>
          <option value="pending">Pending</option>
          <option value="rejected">Rejected</option>
        </select>

        {mobile && <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-4 block">Due EMIs</label>}
        <select value={filterDue} onChange={handleFilterChange(setFilterDue)} className={selectClass}>
          <option value="">All Dues</option>
          <option value=">1">1 – 4 Dues</option>
          <option value=">5">5 – 9 Dues</option>
          <option value=">10">10 – 14 Dues</option>
          <option value=">15">15 – 19 Dues</option>
          <option value=">20">20+ Dues</option>
        </select>

        {mobile && <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-4 block">Paid EMIs</label>}
        <select value={filterInstallment} onChange={handleFilterChange(setFilterInstallment)} className={selectClass}>
          <option value="">All Paid</option>
          <option value="0">0 Paid</option>
          <option value="1-5">1 - 5</option>
          <option value="6-10">6 - 10</option>
          <option value="11-15">11 - 15</option>
          <option value="16-19">16 - 19</option>
          <option value="20-24">20 - 24</option>
        </select>

        {mobile && <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-4 block">Direct Childs</label>}
        <select value={filterChilds} onChange={handleFilterChange(setFilterChilds)} className={selectClass}>
          <option value="">All Childs</option>
          <option value="0">0 Childs</option>
          <option value="1-50">1 - 50</option>
          <option value="51-100">51 - 100</option>
          <option value="101-200">101 - 200</option>
          <option value="201-500">201 - 500</option>
          <option value="500+">500+</option>
        </select>
      </>
    );
  };

  // ============================================================================
  // RENDER 1: DETAIL VIEW
  // ============================================================================
  if (selectedCustomer) {
    return (
      <div className="animate-in fade-in zoom-in-95 duration-300 h-full">
        <CustomerDashboard
          user={selectedCustomer}
          onBack={() => setSelectedCustomer(null)}
          rootLabel="Dashboard"
          rootPath="/dashboard"
          backLabel="My Network"
        />
      </div>
    );
  }

  // ============================================================================
  // RENDER 2: LIST VIEW
  // ============================================================================
  return (
    <div className="animate-in fade-in duration-300 text-slate-900 dark:text-white flex flex-col h-full relative">
      <DownloadToast show={downloadToast.show} filename={downloadToast.filename} onClose={() => setDownloadToast({ show: false, filename: '' })} />

      {/* ── BREADCRUMB ── */}
      <nav className="mb-3 flex-shrink-0">
        <ol className="flex items-center whitespace-nowrap">
          <li className="inline-flex items-center">
            <Link className="flex items-center text-sm text-slate-500 dark:text-slate-400 hover:text-indigo-600 transition-colors font-semibold" to="/dashboard">Dashboard</Link>
            <svg className="shrink-0 mx-2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="m9 18 6-6-6-6"/></svg>
          </li>
          <li className="text-sm font-semibold text-slate-800 dark:text-slate-200">My Customers</li>
        </ol>
      </nav>

      {/* ── HEADER & PRIMARY ACTIONS ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-5 gap-4 flex-shrink-0">
        <div>
          <h2 className="text-2xl font-bold leading-tight">My Network</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            Manage your downline · <span className="font-semibold text-slate-700 dark:text-slate-200">{totalRecords}</span> total members
          </p>
        </div>
        
        {/* Action Buttons in ONE row */}
        <div className="flex items-center gap-2 w-full sm:w-auto flex-shrink-0">
          <button onClick={fetchCustomers} title="Refresh Data" className="p-2.5 sm:px-3 sm:py-2 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-md border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors shadow-sm flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 11A8.1 8.1 0 004.5 9M4 5v4h4M4 13a8.1 8.1 0 0015.5 2M20 19v-4h-4" /></svg>
            <span className="hidden sm:inline sm:ml-1.5 font-medium text-sm">Refresh</span>
          </button>

          <button onClick={handleExportCSV} title="Export to CSV" className="p-2.5 sm:px-3 sm:py-2 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 rounded-md border border-emerald-200 dark:border-emerald-800/50 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-colors shadow-sm flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
            <span className="hidden sm:inline sm:ml-1.5 font-bold text-sm">Export</span>
          </button>

          <button onClick={() => setIsInviteModalOpen(true)} className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 sm:px-5 py-2.5 sm:py-2 rounded-md font-bold text-sm transition-colors shadow-sm whitespace-nowrap">
            <svg className="w-5 h-5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" /></svg>
            Grow Network
          </button>
        </div>
      </div>

      {/* ── TOOLBAR (SEARCH & FILTERS) ── */}
      <div className="bg-white dark:bg-slate-800 p-3 rounded-t-lg border border-slate-200 dark:border-slate-700 border-b-0 flex flex-col xl:flex-row justify-between items-start xl:items-center gap-3">
        
        {/* Left: Search & Filter Row */}
        <div className="flex flex-col lg:flex-row items-start lg:items-center gap-2 w-full xl:w-auto flex-1">
          
          <div className="flex items-center gap-2 w-full lg:w-auto">
            {/* Search */}
            <div className="relative flex-1 lg:w-64 flex-shrink-0">
              <svg className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
              <input type="text" placeholder="Search name, phone..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pl-9 pr-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md text-sm outline-none focus:border-indigo-500" />
            </div>
            
            {/* Mobile Filter Button (Locked in same row) */}
            <button onClick={() => setIsMobileFilterOpen(true)} className="lg:hidden flex-shrink-0 flex items-center justify-center gap-1.5 px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md text-sm font-bold text-slate-700 dark:text-slate-300 hover:bg-slate-100 transition-colors shadow-sm">
              <svg className="w-4 h-4 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" /></svg>
              <span className="hidden min-[375px]:inline">Filters</span>
              {hasActiveFilters && <span className="w-2 h-2 rounded-full bg-indigo-500 ml-0.5"></span>}
            </button>
          </div>

          {/* Desktop Filters */}
          <div className="hidden lg:flex items-center gap-2">
            <FilterSelects mobile={false} />
          </div>
        </div>

        {/* Right: Reset & Count */}
        <div className="flex items-center justify-between w-full xl:w-auto border-t border-slate-100 dark:border-slate-700 xl:border-t-0 pt-3 xl:pt-0 gap-3">
          <div>
            {hasActiveFilters && (
              <button onClick={handleResetFilters} className="flex items-center gap-1.5 px-3 py-2 bg-red-50 text-red-600 border border-red-200 rounded-md hover:bg-red-100 font-bold text-sm dark:bg-red-900/20 dark:border-red-800/50">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" /><line x1="2" y1="2" x2="22" y2="22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
                Reset
              </button>
            )}
          </div>
          <div className="flex items-center gap-3 ml-auto">
            <div className="hidden xl:block w-px h-5 bg-slate-200 dark:bg-slate-700"></div>
            <p className="text-sm font-bold text-slate-500 whitespace-nowrap">
              Showing: <span className="text-slate-900 dark:text-white ml-1 text-base">{filteredCustomers.length}</span>
            </p>
          </div>
        </div>
      </div>

      {/* ── MOBILE FILTER MODAL (BOXY THEME) ── */}
      {isMobileFilterOpen && createPortal(
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-200" onClick={() => setIsMobileFilterOpen(false)}>
          <div className="bg-white dark:bg-slate-800 rounded-md w-full max-w-sm p-5 shadow-2xl relative border border-slate-200 dark:border-slate-700 animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2 pb-3 border-b border-slate-100 dark:border-slate-700">
              <h3 className="font-bold text-lg text-slate-800 dark:text-slate-100 flex items-center gap-2">
                <svg className="w-5 h-5 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" /></svg>
                Apply Filters
              </h3>
              <button onClick={() => setIsMobileFilterOpen(false)} className="p-1 text-slate-400 hover:text-red-500 transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            </div>
            
            <div className="py-2">
              <FilterSelects mobile={true} />
            </div>

            <div className="mt-5 pt-4 border-t border-slate-100 dark:border-slate-700 flex gap-3">
              <button onClick={handleResetFilters} className="flex-1 py-2.5 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 font-bold text-sm rounded border border-slate-200 dark:border-slate-600 hover:bg-slate-200 transition-colors">Reset</button>
              <button onClick={() => setIsMobileFilterOpen(false)} className="flex-1 py-2.5 bg-indigo-600 text-white font-bold text-sm rounded hover:bg-indigo-700 transition-colors shadow-sm">Apply Filters</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {error && <div className="p-4 bg-red-50 text-red-700 text-sm font-bold border-x border-red-200">{error}</div>}

      {/* ── TABLE ── */}
      <div className="bg-white dark:bg-slate-800 rounded-b-lg shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col flex-1 min-h-[65vh]">
        <div className="w-full overflow-x-auto flex-1">
          <table className="w-full text-left border-collapse whitespace-nowrap min-w-max">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">
                <th className="px-4 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-center">Sr.</th>
                <th className="px-4 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-center">View</th>
                <th className="px-4 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Name</th>
                <th className="px-4 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Phone</th>
                <th className="px-4 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Address</th>
                <th className="px-4 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-center">Account</th>
                <th className="px-4 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-center">Joined Date</th>
                <th className="px-4 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-center">Paid</th>
                <th className="px-4 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-center">Due</th>
                <th className="px-4 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-center">Childs</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
              {loading ? (
                <tr><td colSpan="10" className="px-6 py-20 text-center text-slate-500">Fetching network data...</td></tr>
              ) : filteredCustomers.length === 0 ? (
                <tr><td colSpan="10" className="px-6 py-20 text-center text-slate-500">No customers found.</td></tr>
              ) : (
                filteredCustomers.map((user, index) => (
                  <tr key={index} className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
                    <td className="px-4 py-4 text-sm font-medium text-slate-500 text-center">{(page - 1) * PAGE_SIZE + index + 1}</td>
                    <td className="px-4 py-4 text-center">
                      <button onClick={() => setSelectedCustomer(user)} title="View Dashboard" className="p-1.5 bg-indigo-50 text-indigo-600 rounded hover:bg-indigo-600 hover:text-white transition-all border border-indigo-100 mx-auto block active:scale-95 shadow-sm dark:bg-indigo-900/20 dark:border-indigo-800/50">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                      </button>
                    </td>
                    <td className="px-4 py-4 text-sm font-bold text-slate-900 dark:text-white">{user.name}</td>
                    <td className="px-4 py-4 text-sm text-slate-600 dark:text-slate-300">+91 {user.phone}</td>
                    <td className="px-4 py-4 text-sm text-slate-600 dark:text-slate-300 truncate max-w-[200px]" title={`${user.address}, ${user.city}`}>
                      {user.address ? `${user.address}, ${user.city}` : '—'}
                    </td>
                    <td className="px-4 py-4 text-center">{getStatusBadge(user.stage)}</td>
                    <td className="px-4 py-4 text-center text-sm text-slate-900 dark:text-white font-medium">{formatDate(user.created_at)}</td>
                    <td className="px-4 py-4 text-center"><span className="text-sm font-bold text-green-700 bg-green-50 px-2.5 py-1 rounded dark:bg-green-900/20 dark:text-green-400">{user.installments || 0}</span></td>
                    <td className="px-4 py-4 text-center"><span className={`text-sm font-bold px-2.5 py-1 rounded ${user.due > 0 ? 'text-red-700 bg-red-50' : 'text-slate-500 bg-slate-50'}`}>{user.due || 0}</span></td>
                    <td className="px-4 py-4 text-center"><span className="text-sm font-bold text-indigo-700 bg-indigo-50 px-2.5 py-1 rounded dark:bg-indigo-900/20 dark:text-indigo-400">{user.child || 0}</span></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
            {!loading && allCustomers.length > 0 && (
              <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between bg-slate-50 dark:bg-slate-900/50 flex-shrink-0">
                <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">
                  Page <span className="font-bold text-slate-900 dark:text-white">{page}</span> of <span className="font-bold text-slate-900 dark:text-white">{totalPages}</span>
                </p>
                <div className="flex gap-2">
                  <button 
                    onClick={() => setPage(p => Math.max(1, p - 1))} 
                    disabled={page === 1} 
                    className="px-4 py-2 text-sm font-bold text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-md hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 transition-colors"
                  >
                    Previous
                  </button>
                  <button 
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))} 
                    disabled={page === totalPages || totalPages === 0} 
                    className="px-4 py-2 text-sm font-bold text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-md hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
      </div>

      {/* ── INVITE MODAL ── */}
      <ShareModal 
        isOpen={isInviteModalOpen} 
        onClose={() => setIsInviteModalOpen(false)} 
        link={inviteLink} // This is your 'CAP-KOL-9824' referral link
      />
    </div>
  );
};

export default MyCustomers;